import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Server, Settings, LogOut, Menu, X, Plus, ChevronRight, Layers } from 'lucide-react';

function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Server },
    { name: 'Plans', href: '/plans', icon: Layers },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900/95 backdrop-blur-sm border-r border-slate-800/50 
          transform transition-transform duration-300 lg:translate-x-0 
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 p-6 border-b border-slate-800/50">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Server className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">VM Pricing</h1>
              <p className="text-xs text-slate-400">Infrastructure Tool</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="ml-auto lg:hidden text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}

            {/* New Config Button */}
            <Link
              to="/config/new"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-4 py-3 mt-4 rounded-xl border border-dashed border-slate-700 
                text-slate-400 hover:text-amber-400 hover:border-amber-500/30 transition-all"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">New Configuration</span>
            </Link>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-800/50">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800/30">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-400 transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-sm border-b border-slate-800/50">
          <div className="flex items-center gap-4 px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-slate-400 hover:text-white transition-colors">
                Dashboard
              </Link>
              {location.pathname !== '/' && (
                <>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                  <span className="text-slate-200">
                    {location.pathname.includes('/config/new')
                      ? 'New Configuration'
                      : location.pathname.includes('/config/')
                      ? 'Edit Configuration'
                      : location.pathname.includes('/plans')
                      ? 'Pricing Plans'
                      : location.pathname.includes('/settings')
                      ? 'Settings'
                      : ''}
                  </span>
                </>
              )}
            </nav>
          </div>
        </header>

        {/* Page content */}
        <main className="relative z-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default Layout;
